import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import Button from 'components/ui/Button';
import LoadingMark from 'components/ui/LoadingMark';
import { pdfAnchorToScreenRect } from 'utils/signatureFieldCoordinates';
import SignatureBox from './SignatureBox';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const MIN_CONTAINER_WIDTH = 16;

const PdfPageViewer = ({
  open,
  pdfBlob,
  fields,
  selectedSignerId,
  currentPage,
  totalPages,
  onPageChange,
  onSelectField,
  onUpdateFieldGeometry,
  onAddField,
  onAddMySignature,
  addingMySignature = false,
  scrollToFieldTrigger = 0,
  loadingPdf = false,
  loadError = null,
  canAddField = true,
  pageDimensions,
  onPageDimensions,
  viewOnly = false,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const boxRefs = useRef({});
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderPageRef = useRef(async () => {});
  const [canvasRect, setCanvasRect] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [containerReady, setContainerReady] = useState(false);

  const scrollSelectedBoxIntoView = useCallback(() => {
    if (!selectedSignerId || !containerRef.current) return;

    const box =
      boxRefs.current[selectedSignerId] ||
      containerRef.current.querySelector(
        `[data-signature-box-id="${selectedSignerId}"]`
      );

    if (!box) return;

    box.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
    if (typeof box.focus === 'function') {
      box.focus({ preventScroll: true });
    }
  }, [selectedSignerId]);

  useEffect(() => {
    if (!selectedSignerId || rendering || loadingPdf || !canvasRect) return undefined;

    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) scrollSelectedBoxIntoView();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [scrollToFieldTrigger, selectedSignerId, rendering, loadingPdf, canvasRect, currentPage, scrollSelectedBoxIntoView]);

  const renderPage = useCallback(async () => {
    if (!open || loadingPdf || !pdfDocRef.current || !canvasRef.current) return;

    const containerWidth = containerRef.current?.clientWidth ?? 0;
    if (containerWidth < MIN_CONTAINER_WIDTH) return;

    setRendering(true);
    try {
      renderTaskRef.current?.cancel?.();
      renderTaskRef.current = null;

      const page = await pdfDocRef.current.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.0 });
      const pdfPageWidthPt = viewport.width;
      const pdfPageHeightPt = viewport.height;

      onPageDimensions?.(currentPage, pdfPageWidthPt, pdfPageHeightPt);

      const innerWidth = Math.max(containerWidth - 32, MIN_CONTAINER_WIDTH);
      const scale = innerWidth / pdfPageWidthPt;
      const scaledViewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      const task = page.render({ canvasContext: context, viewport: scaledViewport });
      renderTaskRef.current = task;
      await task.promise;

      setCanvasRect({
        width: scaledViewport.width,
        height: scaledViewport.height,
        left: 0,
        top: 0,
      });
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') {
        console.error('PdfPageViewer render error:', err);
      }
    } finally {
      setRendering(false);
    }
  }, [currentPage, loadingPdf, onPageDimensions, open]);

  renderPageRef.current = renderPage;

  useEffect(() => {
    if (!open || !pdfBlob) {
      pdfDocRef.current = null;
      setCanvasRect(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await pdfBlob.arrayBuffer();
        if (cancelled) return;
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        await renderPageRef.current();
      } catch (err) {
        if (!cancelled) {
          console.error('PdfPageViewer load error:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
      renderTaskRef.current = null;
      pdfDocRef.current = null;
    };
  }, [open, pdfBlob]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateReady = () => {
      const width = container.clientWidth;
      setContainerReady(width >= MIN_CONTAINER_WIDTH);
    };

    updateReady();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateReady();
        void renderPageRef.current();
      });
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateReady);
    return () => window.removeEventListener('resize', updateReady);
  }, [open, loadingPdf]);

  useEffect(() => {
    if (!loadingPdf && containerReady) {
      void renderPage();
    }
  }, [renderPage, loadingPdf, containerReady, currentPage]);

  const pageFields = fields.filter((f) => f.page_number === currentPage);
  const dims = pageDimensions?.[currentPage];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Mobile / tablet: page navigation only — actions live in SpeedDialFab */}
      <div className="lg:hidden flex items-center justify-center gap-2 px-2 py-1.5 border-b border-border bg-muted/30 flex-shrink-0">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="h-9 w-9 shrink-0 px-0"
          iconName="ChevronLeft"
          disabled={currentPage <= 1 || loadingPdf || rendering}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Página anterior"
          title="Página anterior"
        />
        <span className="text-xs text-foreground whitespace-nowrap tabular-nums min-w-[7rem] text-center">
          Página {currentPage} de {totalPages || '—'}
        </span>
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="h-9 w-9 shrink-0 px-0"
          iconName="ChevronRight"
          disabled={currentPage >= totalPages || loadingPdf || rendering}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Próxima página"
          title="Próxima página"
        />
      </div>

      {/* Desktop: single toolbar row with labels */}
      <div className="hidden lg:flex items-center justify-between gap-2 px-4 py-2 border-b border-border bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            disabled={currentPage <= 1 || loadingPdf || rendering}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Página anterior"
          />
          <span className="text-sm text-foreground whitespace-nowrap">
            Página {currentPage} de {totalPages || '—'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            disabled={currentPage >= totalPages || loadingPdf || rendering}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Próxima página"
          />
        </div>
        {!viewOnly ? (
          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            <Button
              type="button"
              size="sm"
              iconName="UserPlus"
              onClick={onAddMySignature}
              disabled={
                !canAddField ||
                loadingPdf ||
                rendering ||
                addingMySignature ||
                !onAddMySignature
              }
              loading={addingMySignature}
            >
              Adicionar Minha Assinatura
            </Button>
            <Button
              type="button"
              size="sm"
              iconName="Plus"
              onClick={onAddField}
              disabled={!canAddField || loadingPdf || rendering || addingMySignature}
            >
              Adicionar campo assinatura
            </Button>
          </div>
        ) : null}
      </div>

      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-muted/40 p-2 lg:p-4 flex justify-center items-start min-h-0 min-w-0 w-full"
      >
        {loadingPdf ? (
          <div className="flex justify-center py-16">
            <LoadingMark message="Carregando PDF…" />
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center max-w-md">
            <p className="text-sm text-destructive">{loadError}</p>
          </div>
        ) : (
          <div className="relative inline-block shadow-md bg-white min-w-[200px]">
            <canvas ref={canvasRef} className="block max-w-full h-auto" />
            {canvasRect && dims && !rendering && !viewOnly && (
              <div
                className="absolute inset-0"
                style={{ width: canvasRect.width, height: canvasRect.height }}
              >
                {pageFields.map((field) => {
                  const screenRect = pdfAnchorToScreenRect(
                    field,
                    canvasRect,
                    field.pdf_page_width_pt,
                    field.pdf_page_height_pt
                  );
                  return (
                    <SignatureBox
                      key={field.signer_id}
                      boxRef={(el) => {
                        if (el) boxRefs.current[field.signer_id] = el;
                        else delete boxRefs.current[field.signer_id];
                      }}
                      field={field}
                      screenRect={screenRect}
                      selected={selectedSignerId === field.signer_id}
                      onSelect={() => onSelectField(field.signer_id)}
                      onGeometryChange={(nextScreenRect) => {
                        onUpdateFieldGeometry(field.signer_id, nextScreenRect, canvasRect, dims);
                      }}
                    />
                  );
                })}
              </div>
            )}
            {(rendering || !canvasRect) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 min-h-[240px]">
                <LoadingMark size="xs" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPageViewer;
